import React, { useMemo } from 'react';
import clsx from 'clsx';
import RateBars from '@/components/RateBars/RateBars';
import { BadgeRating } from '@/components/Badge/Badge';
import { getRatingMessage, getVerdict, Verdict } from '@/helpers/ratingState';
import warningSymbol from '@/assets/warning-symbol.png';
import okSymbol from '@/assets/ok-symbol.png';
import noSymbol from '@/assets/no-symbol.png';
import { CardSectionRateWideProps } from './CardSectionWide.types';
import bgNeutral from './bg-neutral.png';
import bgOk from './bg-ok.png';

export const CardSectionRateWide: React.FC<CardSectionRateWideProps> = ({
  title,
  rating = 1,
  maxRating = 5,
  description,
  ratingMessage: customRatingMessage,
  className,
}) => {
  const ratingMessage = useMemo(() => {
    return customRatingMessage || getRatingMessage(rating);
  }, [rating, customRatingMessage]);

  const img = useMemo(() => {
    const verdict = getVerdict(rating);
    if (verdict === Verdict.NEGATIVE) return noSymbol;
    if (verdict === Verdict.NEUTRAL) return warningSymbol;
    if (verdict === Verdict.POSITIVE) return okSymbol;
    return okSymbol;
  }, [rating]);

  const cardVerdict = useMemo(() => {
    const verdict = getVerdict(rating);
    if (verdict === Verdict.NEGATIVE) return 'negative';
    if (verdict === Verdict.NEUTRAL) return 'neutral';
    if (verdict === Verdict.POSITIVE) return 'positive';
    return 'positive';
  }, [rating]);

  return (
    <div
      className={clsx(
        'relative max-w-[836px] overflow-hidden rounded-3xl border border-solid border-emerald-300 shadow-2xl w-full',
        className,
        {
          'bg-negative': cardVerdict === 'negative',
          'bg-neutral': cardVerdict === 'neutral',
          'bg-positive': cardVerdict === 'positive',
        },
      )}
    >
      <div
        className="absolute inset-0 top-0 right-0 bottom-0 bg-no-repeat bg-cover lg:bg-[75%]"
        style={{
          backgroundImage: `url(${getVerdict(rating) === Verdict.NEUTRAL ? bgNeutral : bgOk
            })`,
          backgroundPosition: '100% 100%',
        }}
      ></div>
      <div className="flex relative flex-wrap gap-6 items-center px-6 pt-6 pb-5 w-full max-md:max-w-full max-md:px-5">
        <div className="flex flex-col self-stretch my-auto">
          <div className="flex flex-col items-start font-bold">
            <div className="text-xl leading-snug text-zinc-800">{title}</div>
            <div className="mt-2">
              <BadgeRating rating={rating} version="v2" />
            </div>
          </div>
          <div className="mt-6 flex min-h-[76px] items-end">
            <RateBars maxRating={maxRating} rating={rating} />
            <div className="flex flex-col">
              <div className="flex items-end">
                <span className="text-[40px] font-extrabold leading-[40px] text-text-primary">
                  {rating}
                </span>
                <span className="pl-2 text-[24px] font-normal leading-[30px] text-[#CDD4E0]">
                  /{maxRating}
                </span>
              </div>
              <div className="max-w-[220px] text-base font-bold text-text-primary">
                {ratingMessage}
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-[2] my-auto min-h-[52px] min-w-40 max-w-[400px] flex-1 shrink basis-0 gap-2.5 self-stretch text-center text-base font-medium leading-7 text-neutral-700 max-md:max-w-full">
          {description}
        </div>
        <div className="absolute bottom-0 right-[25%] z-0 max-w-[230px]">
          <img src={img} alt="rating" />
        </div>
      </div>
    </div>
  );
};

export default CardSectionRateWide;
